import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { QuotationBlock as QuotationBlockType } from './types';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { 
  ChevronDown, 
  ChevronRight, 
  MapPin, 
  Trash, 
  Calendar, 
  ImagePlus, 
  CircleDot,
  AlignLeft,
  Image,
  Box,
  Bookmark,
  Copy,
  ArrowRight,
  MoreHorizontal,
  RefreshCw
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { formatSafeDate } from '@/lib/date-utils';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { SaveToLibraryModal } from './SaveToLibraryModal';
import { isItemSavedInLibrary } from '@/lib/library-utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageSelectionModal } from './PageSelectionModal';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { globalMoveBlockToPage as globalMoveBlockToPageFunc } from '@/hooks/useQuotationForm';

interface QuotationBlockProps {
  block: QuotationBlockType;
  index: number;
  pageId: string;
  updateBlock: (pageId: string, blockId: string, field: keyof QuotationBlockType, value: any) => void;
  deleteBlock: (pageId: string, blockId: string) => void;
  duplicateBlock?: (pageId: string, blockId: string) => void;
  moveBlockToPage?: (sourcePageId: string, blockId: string, targetPageId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  allPages?: any[]; // All pages in the quotation
}

// Custom styled label with icon
const StyledLabel = ({ 
  children, 
  className, 
  icon: Icon = CircleDot 
}: { 
  children: React.ReactNode; 
  className?: string; 
  icon?: React.ElementType
}) => (
  <div className={cn("flex items-center gap-1", className)}>
    <Icon className="h-3.5 w-3.5 text-gray-500" />
    <Label className="text-gray-500 font-medium uppercase tracking-wide">{children}</Label>
  </div>
);

// Input with consistent formatting
const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm", className)}
    {...props}
  />
);

export function QuotationBlock({
  block,
  index,
  pageId,
  updateBlock,
  deleteBlock,
  duplicateBlock,
  moveBlockToPage,
  isCollapsed,
  onToggleCollapse,
  allPages = []
}: QuotationBlockProps) {
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [isSaveToLibraryOpen, setIsSaveToLibraryOpen] = useState(false);
  const [isProcessingDescription, setIsProcessingDescription] = useState(false);
  const [isSavedToLibrary, setIsSavedToLibrary] = useState(false);
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);
  const [blockTitle, setBlockTitle] = useState<string>(() => {
    return block.title || `Bloco #${index + 1}`;
  });
  const [selectedBlockId, setSelectedBlockId] = useState<string>('');

  // Effect to keep blockTitle in sync with block.title changes
  useEffect(() => {
    const displayTitle = block.title?.trim() ? block.title : `Bloco #${index + 1}`;
    setBlockTitle(displayTitle);
  }, [block.title, index]);

  // Handler for finishing title edit
  const handleFinishTitleEdit = () => {
    if (blockTitle.trim() !== '') {
      updateBlock(pageId, block.id, 'title', blockTitle.trim());
    } else {
      updateBlock(pageId, block.id, 'title', '');
      setBlockTitle(`Bloco #${index + 1}`);
    }
  };

  const handleUpdateImages = (images: string[]) => {
    updateBlock(pageId, block.id, 'images', images);
  };
  
  // Função para atualizar múltiplos campos do bloco de uma vez
  const batchUpdateBlock = (updates: Partial<QuotationBlockType>) => {
    console.log('[QuotationBlock] Batch updating block:', {
      blockId: block.id,
      updates
    });
    
    // Aplica as atualizações em um único passo
    Object.entries(updates).forEach(([field, value]) => {
      updateBlock(pageId, block.id, field as keyof QuotationBlockType, value);
    });
  };
  
  // Handler para quando um local é selecionado no PlacesAutocompleteV1
  const handlePlaceSelect = (placeData: {
    placeId: string;
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
    googleMapsUrl: string;
    city: string;
    country: string;
  }) => {
    // Criar um objeto com todas as atualizações relacionadas ao local
    const locationUpdates: Partial<QuotationBlockType> = {
      placeId: placeData.placeId,
      location: placeData.name,
      coordinates: placeData.coordinates ? {
        lat: Number(placeData.coordinates.lat),
        lng: Number(placeData.coordinates.lng)
      } : undefined,
      googleMapsUrl: placeData.googleMapsUrl,
      city: placeData.city,
      country: placeData.country
    };
    
    console.log('[QuotationBlock] Place selected with data:', {
      name: placeData.name,
      city: placeData.city,
      country: placeData.country,
      coordinates: placeData.coordinates
    });
    
    // Atualizar todos os campos de uma vez
    batchUpdateBlock(locationUpdates);
    
    // Mostrar toast de confirmação
    toast({
      title: "Local adicionado",
      description: `${placeData.name} foi vinculado a este bloco.`,
    });
  };
  
  // Verificar se o bloco já está salvo na biblioteca
  useEffect(() => {
    let isMounted = true;
    
    const checkLibraryStatus = async () => {
      try {
        const isSaved = await isItemSavedInLibrary(block, 'block');
        if (isMounted) {
          setIsSavedToLibrary(isSaved);
        }
      } catch (error) {
        console.error('Erro ao verificar status na biblioteca:', error);
        if (isMounted) {
          setIsSavedToLibrary(false);
        }
      }
    };
    
    checkLibraryStatus();
    
    // Recarregar status quando o bloco for atualizado
    const interval = setInterval(checkLibraryStatus, 5000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [block, block.id]);

  // Handler para o botão de salvar na biblioteca
  const handleSaveToLibrary = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o clique colapse/expanda o item
    setIsSaveToLibraryOpen(true);
  };

  // AI Description enhancement
  const handleProcessDescription = async () => {
    if (!block.description || block.description.trim() === '') {
      toast({
        title: "Texto vazio",
        description: "Por favor, insira um texto para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessingDescription(true);
    
    try {
      // Extract text from HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = block.description;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      if (!textContent.trim()) {
        toast({
          title: "Texto vazio",
          description: "Por favor, insira um texto para ser processado pela IA.",
          variant: "destructive"
        });
        setIsProcessingDescription(false);
        return;
      }

      toast({
        title: "Processando texto",
        description: "Enviando seu texto para processamento com IA...",
      });
      
      // Enviar apenas o texto do usuário, sem contexto ou instruções adicionais
      // Increase maxTokens to avoid truncation
      const response = await chatCompletion(textContent, 'gpt-4-turbo', 0.7, 1500);
      
      // Extrair a resposta do ChatGPT
      const generatedContent = response.choices[0].message.content.trim();
      
      // Check if the response was truncated
      if (response.choices[0].finish_reason === 'length') {
        toast({
          title: "Aviso de truncamento",
          description: "O texto gerado foi truncado devido ao limite de tamanho. Considere dividir o conteúdo em partes menores.",
          variant: "default"
        });
      }
      
      // Atualizar o campo de descrição
      updateBlock(pageId, block.id, 'description', generatedContent);
      
      toast({
        title: "Texto gerado",
        description: "O texto foi processado com IA e atualizado com sucesso.",
      });
    } catch (error) {
      console.error('Erro ao processar texto com IA:', error);
      
      toast({
        title: "Erro no processamento",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao processar o texto com IA.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingDescription(false);
    }
  };

  // Handler para iniciar o processo de mover o bloco para outra página
  const handleInitiateBlockMove = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevenir propagação do evento
    
    console.log('[QuotationBlock] Iniciando movimento de bloco', {
      blockId: block.id,
      pageId: pageId
    });
    
    if (!block.id) {
      console.error('[QuotationBlock] Erro: ID do bloco não definido');
      toast({
        title: "Erro ao mover bloco",
        description: "ID do bloco não definido",
        variant: "destructive"
      });
      return;
    }

    // Verificar se temos allPages e se é um array com pelo menos uma página
    if (!Array.isArray(allPages) || allPages.length <= 1) {
      console.log('[QuotationBlock] Movimento não disponível - não há outras páginas', {
        allPagesIsArray: Array.isArray(allPages),
        allPagesLength: Array.isArray(allPages) ? allPages.length : 'não é array'
      });
      toast({
        title: "Movimento não disponível",
        description: "Não existem outras páginas para mover este bloco",
        variant: "destructive"
      });
      return;
    }

    // Verificar se temos a função moveBlockToPage ou o fallback global
    if (typeof moveBlockToPage !== 'function' && typeof globalMoveBlockToPageFunc !== 'function') {
      console.error('[QuotationBlock] Erro: Função de movimento não disponível');
      toast({
        title: "Funcionalidade indisponível",
        description: "A função para mover blocos não está disponível",
        variant: "destructive"
      });
      return;
    }

    // Filtrar a página atual das opções
    const availablePages = allPages.filter(p => p.id !== pageId);
    
    if (availablePages.length === 0) {
      toast({
        title: "Movimento não disponível",
        description: "Não existem outras páginas para mover este bloco",
        variant: "destructive"
      });
      return;
    }

    // Armazenar o ID do bloco e abrir o modal de seleção
    setSelectedBlockId(block.id);
    setIsPageSelectionOpen(true);
  };

  // Função para executar o movimento após a seleção da página
  const handleMoveToPage = (targetPageId: string) => {
    try {
      if (!selectedBlockId) {
        console.error('[QuotationBlock] Erro: ID do bloco não selecionado');
        toast({
          title: "Erro ao mover bloco",
          description: "ID do bloco não selecionado",
          variant: "destructive"
        });
        return;
      }

      // Criar cópia do bloco para debug futuro
      const blockToMove = block;
      if (blockToMove) {
        console.log('[QuotationBlock] Cópia do bloco antes de mover:', JSON.stringify(blockToMove));
        localStorage.setItem('lastMovedBlock', JSON.stringify({
          block: blockToMove,
          sourcePageId: pageId,
          targetPageId,
          timestamp: new Date().toISOString()
        }));
      }
      
      // Usar função local ou global
      if (typeof moveBlockToPage === 'function') {
        console.log('[QuotationBlock] Usando função moveBlockToPage recebida como prop');
        moveBlockToPage(pageId, selectedBlockId, targetPageId);
      } else if (typeof globalMoveBlockToPageFunc === 'function') {
        console.log('[QuotationBlock] Usando função global moveBlockToPage');
        globalMoveBlockToPageFunc(pageId, selectedBlockId, targetPageId);
      }
      
      toast({
        title: "Bloco movido",
        description: "O bloco foi movido para a página selecionada",
      });
    } catch (error) {
      console.error('[QuotationBlock] Erro ao mover bloco:', error);
      toast({
        title: "Erro ao mover bloco",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      // Limpar estado
      setSelectedBlockId('');
      setIsPageSelectionOpen(false);
    }
  };

  const locationDiffersFromTitle = !!blockTitle && (block.location === '' || block.location === undefined || blockTitle !== block.location);
  const handleSyncLocationWithTitle = () => {
    if (blockTitle) {
      updateBlock(pageId, block.id, 'location', blockTitle);
    }
  };

  return (
    <Card className={cn(
      "border shadow-sm transition-all border-l-4 border-l-[#fbf0e2]",
      isCollapsed && "border-transparent border-l-[#fbf0e2]"
    )}>
      <CardHeader 
        className={cn(
          "cursor-pointer flex items-center justify-between",
          isCollapsed ? "py-2 px-3" : "py-3 px-4"
        )}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2 w-full">
          <Box className="h-4 w-4 text-[#e6a23c]" />
          {isCollapsed ? (
            <span className="font-medium">{block.title || `Bloco #${index + 1}`}</span>
          ) : (
            <FormInput
              value={blockTitle}
              onChange={(e) => setBlockTitle(e.target.value)}
              onFocus={() => {
                // Se for apenas o placeholder, limpe o campo para edição
                if (!block.title || block.title.trim() === '') {
                  setBlockTitle('');
                }
              }}
              onBlur={handleFinishTitleEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleFinishTitleEdit();
                }
              }}
              className="border-none h-8 text-base font-medium focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
              placeholder={`Bloco #${index + 1}`}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          
          {/* Quick info for collapsed view */}
          {isCollapsed && block.location && (
            <div className="flex items-center ml-3 text-sm text-gray-500">
              <MapPin className="h-3.5 w-3.5 mr-1" />
              <span className="truncate">{block.location}</span>
            </div>
          )}
          
          <div className="flex items-center ml-auto">
            {/* Botão de Salvar na Biblioteca (bookmark) */}
            <Button
              variant={isSavedToLibrary ? "default" : "ghost"}
              size="icon"
              className={cn(
                "h-8 w-8 transition-colors",
                isSavedToLibrary 
                  ? "bg-amber-500 hover:bg-amber-600" 
                  : "hover:text-amber-500"
              )}
              onClick={handleSaveToLibrary}
              title={isSavedToLibrary ? "Item salvo na biblioteca" : "Salvar na Biblioteca"}
            >
              <Bookmark className={cn(
                "h-4 w-4", 
                isSavedToLibrary 
                  ? "text-white" 
                  : "text-gray-500 hover:text-amber-500"
              )} />
            </Button>
            
            {/* Dropdown Menu de Ações */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                >
                  <MoreHorizontal className="h-4 w-4 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" className="w-40">
                {duplicateBlock && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicateBlock(pageId, block.id);
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    <span>Duplicar</span>
                  </DropdownMenuItem>
                )}
                
                {/* Nova opção de Mover para outra página */}
                {allPages && allPages.length > 1 && (
                  <DropdownMenuItem onClick={handleInitiateBlockMove}>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    <span>Mover</span>
                  </DropdownMenuItem>
                )}
                
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteBlock(pageId, block.id);
                  }}
                  className="text-red-600"
                >
                  <Trash className="h-4 w-4 mr-2" />
                  <span>Excluir</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="p-3 pt-0 space-y-3">
          <div className="space-y-1 flex flex-col">
            <StyledLabel className="text-xs" icon={MapPin}>Localização</StyledLabel>
            <div className="flex items-center gap-2 w-full">
              <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex-grow w-full">
                <div className="relative">
                  <PlacesAutocompleteV1
                    value={block.location || ''}
                    onChange={(value) => updateBlock(pageId, block.id, 'location', value)}
                    onPlaceSelect={handlePlaceSelect}
                    placeholder="Digite um local"
                    className="w-full"
                    showNameInsteadOfAddress={true}
                  />
                  {locationDiffersFromTitle && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-12 top-0 h-10 w-10"
                      onClick={handleSyncLocationWithTitle}
                      title="Sincronizar localização com o título"
                    >
                      <RefreshCw className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {block.googleMapsUrl && (
              <div className="text-xs text-right mt-1">
                <a 
                  href={block.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-voah-teal hover:underline"
                >
                  Ver no Google Maps
                </a>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1 flex flex-col">
              <StyledLabel className="text-xs" icon={Calendar}>Data de início</StyledLabel>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                <FormInput
                  type="date"
                  value={block.startDate || ''}
                  onChange={(e) => updateBlock(pageId, block.id, 'startDate', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <div className="space-y-1 flex flex-col">
              <StyledLabel className="text-xs" icon={Calendar}>Data de fim</StyledLabel>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                <FormInput
                  type="date"
                  value={block.endDate || ''}
                  onChange={(e) => updateBlock(pageId, block.id, 'endDate', e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1 flex flex-col">
            <StyledLabel className="text-xs" icon={AlignLeft}>Descrição</StyledLabel>
            <RichTextEditor
              value={block.description || ''}
              onChange={(value) => updateBlock(pageId, block.id, 'description', value)}
              placeholder="Descreva este bloco de cotação..."
              rows={4}
              onProcessWithAI={handleProcessDescription}
              isProcessing={isProcessingDescription}
            />
          </div>

          <div className="space-y-1 flex flex-col">
            <StyledLabel className="text-xs" icon={Image}>Imagens</StyledLabel>
            <div className="flex flex-wrap gap-2">
              {block.images && block.images.length > 0 ? (
                block.images.map((image, idx) => (
                  <div key={idx} className="relative group h-16 w-16">
                    <img 
                      src={image} 
                      alt={`Imagem ${idx + 1}`} 
                      className="h-full w-full object-cover rounded-md border border-gray-200"
                    />
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500">Nenhuma imagem adicionada</div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsImageManagerOpen(true)}
                className="h-16 w-16 flex flex-col items-center justify-center"
              >
                <ImagePlus className="h-5 w-5 mb-1" />
                <span className="text-xs">Adicionar</span>
              </Button>
            </div>
          </div>
        </CardContent>
      )}

      {/* Image Manager Modal */}
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={block.images || []}
          onImagesChange={handleUpdateImages}
          contextId={block.id}
          contextType="customSection"
        />
      )}
      
      {/* Modal de Salvar na Biblioteca */}
      {isSaveToLibraryOpen && (
        <SaveToLibraryModal
          isOpen={isSaveToLibraryOpen}
          onClose={() => setIsSaveToLibraryOpen(false)}
          item={block}
          itemType="block"
          onSave={(savedItem) => {
            setIsSavedToLibrary(true);
            toast({
              title: "Item salvo na biblioteca",
              description: "O bloco foi salvo com sucesso na sua biblioteca.",
            });
            setIsSaveToLibraryOpen(false);
          }}
        />
      )}

      {/* Modal de Seleção de Página */}
      {isPageSelectionOpen && (
        <PageSelectionModal
          isOpen={isPageSelectionOpen}
          onClose={() => setIsPageSelectionOpen(false)}
          onPageSelect={handleMoveToPage}
          pages={allPages.filter(p => p.id !== pageId)}
          title="Selecione uma página para mover o bloco"
        />
      )}
    </Card>
  );
}